import type { IntlShape } from 'react-intl';

import { i18n } from './changeHistoryMacroI18n.ts';
import type {
	ChangeHistoryAuthor,
	ChangeHistoryContent,
	ChangeHistoryCurrentVersion,
	ChangeHistoryData,
	ChangeHistoryVersion,
} from './changeHistoryMacroTypes.ts';

const SUPPORTED_CONTENT_TYPES = new Set(['page', 'blogpost']);
const LIVE_DOC_SUBTYPE = 'live';

type AuthorWithAccountId = NonNullable<ChangeHistoryAuthor> & {
	accountId?: string | null;
};

const hasAccountId = (author: NonNullable<ChangeHistoryAuthor>): author is AuthorWithAccountId =>
	'accountId' in author;

export const getSupportedContentError = (formatMessage: IntlShape['formatMessage']): Error =>
	new Error(formatMessage(i18n.unsupportedContent));

export const isSupportedContent = (content: ChangeHistoryContent | null | undefined): boolean => {
	if (!content) {
		return false;
	}

	return SUPPORTED_CONTENT_TYPES.has(content.type ?? '') || content.subType === LIVE_DOC_SUBTYPE;
};

export const getAuthorName = (
	author: ChangeHistoryAuthor | null | undefined,
	formatMessage: IntlShape['formatMessage'],
): string => author?.displayName || formatMessage(i18n.unknownUser);

export const getAuthorAccountId = (
	author: ChangeHistoryAuthor | null | undefined,
): string | undefined => {
	if (!author || !hasAccountId(author)) {
		return undefined;
	}

	return author.accountId ?? undefined;
};

const toChangeHistoryVersion = (
	version: ChangeHistoryCurrentVersion | null | undefined,
): ChangeHistoryVersion | null => {
	if (!version?.number) {
		return null;
	}

	return {
		by: version.by,
		friendlyWhen: version.friendlyWhen ?? '',
		message: version.message ?? '',
		number: version.number,
		when: version.when ?? '',
	} as ChangeHistoryVersion;
};

const isChangeHistoryVersion = (
	version: ChangeHistoryVersion | null | undefined,
): version is ChangeHistoryVersion => Boolean(version);

export const getChangeHistoryVersions = (
	contentHistory: ChangeHistoryData | null | undefined,
	currentVersion?: ChangeHistoryCurrentVersion | null,
): ChangeHistoryVersion[] => {
	const versions = (contentHistory?.nodes ?? []).filter(isChangeHistoryVersion);

	return versions.length
		? versions
		: [toChangeHistoryVersion(currentVersion)].filter(isChangeHistoryVersion);
};
